import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { CSSProperties } from 'react';

import { AppShell } from '@/components/AppShell';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { HaiSelect } from '@/components/HaiSelect';
import { HaiIcon } from '@/components/icons';
import { PageTopBar } from '@/components/PageTopBar';
import { services } from '@/services';
import type { ProjectListOptions } from '@/services/projectsService';
import type { BrandKit, Project } from '@/types';

import { ProjectCreateDialog } from '@/features/projectCreate/ProjectCreateDialog';

import { ProjectCard, ProjectRow } from './ProjectListItems';
import { ProjectDeleteDialog } from './ProjectDeleteDialog';
import { ProjectEditDialog } from './ProjectEditDialog';
import {
  countLineStyle,
  dashedRowPlusBoxStyle,
  dashedRowStyle,
  dashedTileLabelStyle,
  dashedTilePlusBoxStyle,
  dashedTileStyle,
  errorBannerStyle,
  gridStyle,
  listStyle,
  loadingStyle,
  primaryBtnStyle,
  searchBoxStyle,
  searchIconStyle,
  searchInputStyle,
  sortSelectStyle,
  viewToggleBtnStyle,
  viewToggleStyle,
} from './projectListStyles';

/**
 * R20 — Project(工作)列表頁
 * -----------------------------------------------------------------------------
 * 顯示 ``services.projectsService.list()`` 結果(已過濾軟刪除);點卡片進
 * `/canvas/{projectId}` 繼續編輯。每張卡片 join brand kit name(用 list 回來的
 * brand kits 做 lookup,避免每張卡 N+1 GET 打死後端)。
 *
 * D3 (3) — M-12 視覺對齊:filter bar(search + sort)/ count line /
 * 卡片整片可點 + 右上 kebab menu / 4 欄 grid + 底部 dashed tile。
 *
 * 搜尋已生效:`query` → `filtered` 走 client-side name/description 過濾(BE list
 * endpoint 不支援 search,本地過濾就夠)。**排序(T12 / docs/50)已接後端** —
 * `sortKey` → `SORT_OPTIONS` 映射成 `sort_by`/`order` query 打 `list()`;改 sort
 * 重新 fetch(BE server-side 排序,非本地)。
 *
 * M-12 view toggle(R-D3v1):filter bar 右側網格 / 列表分段切換(對齊 mockup
 * line 198-205)。`view` state 控制 grid(預設,卡片 4 欄)vs list(精簡列)兩種
 * 呈現;兩者共用同一份 `filtered` 資料 + ProjectCard/ProjectRow 各自渲染。
 *
 * **R206 拆分後本檔是頁面層**:卡片 / 列 = [ProjectListItems](./ProjectListItems.tsx)、
 * kebab 選單 = [KebabMenu](./KebabMenu.tsx)、cover gradient = [projectCovers](./projectCovers.ts)、
 * 刪除確認 = [ProjectDeleteDialog](./ProjectDeleteDialog.tsx)、頁面 styles =
 * [projectListStyles](./projectListStyles.ts)。
 */
type ViewMode = 'grid' | 'list';

/**
 * 排序下拉的 3 個選項 → BE `sort_by`/`order`(T12 / docs/50)。dropdown 顯 i18n
 * label,key 維持穩定不隨語系變。
 */
type SortKey = 'recent' | 'created' | 'name';

const SORT_OPTIONS: Record<SortKey, ProjectListOptions> = {
  recent: { sortBy: 'updated_at', order: 'desc' },
  created: { sortBy: 'created_at', order: 'desc' },
  name: { sortBy: 'name', order: 'asc' },
};

interface ProjectListPageProps {
  /**
   * 掛載即打開「建立新專案」燈箱。只有 `/new` route 會傳 true —— 首頁 CTA 與既有
   * 深連結都指那個網址,D3 的建立表單本身則是這一頁上的燈箱(`openForm('create')`
   * 開 `.pj-modal`,不換頁)。
   */
  autoOpenCreate?: boolean;
}

export function ProjectListPage({ autoOpenCreate = false }: ProjectListPageProps = {}) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [brandKits, setBrandKits] = useState<BrandKit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Project | null>(null);
  // R292(FB-20260828-0008)— 編輯專案資訊 dialog 的目標。
  const [editing, setEditing] = useState<Project | null>(null);
  // R293 — 建立新專案改成 D3 燈箱(`ProjectCreateDialog`),開在這一頁上。
  // `/new` route 進來時 `autoOpenCreate` 為 true(首頁 CTA / 深連結仍走得通)。
  const [creating, setCreating] = useState(autoOpenCreate);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<ViewMode>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('recent');

  const refresh = useCallback(async () => {
    try {
      // 並行抓 projects + brandKits — 卡片需要 brandKit name 顯示。
      // T12:projects 帶 sort_by/order 走 BE server-side 排序。
      // R397 — 品牌規範 UI 隱藏(brandKitsGate):gate 關不抓 kits(chip 也不畫,見 Items)。
      const [projectList, kitList] = await Promise.all([
        services.projectsService.list(SORT_OPTIONS[sortKey]),
        BRAND_KITS_UI_VISIBLE ? services.brandKitsService.list() : Promise.resolve([]),
      ]);
      setProjects(projectList);
      setBrandKits(kitList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [sortKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const brandKitById = useMemo(() => {
    const map = new Map<string, BrandKit>();
    for (const kit of brandKits) {
      map.set(kit.brandKitId, kit);
    }
    return map;
  }, [brandKits]);

  const filtered = useMemo(() => {
    if (!projects) return null;
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false),
    );
  }, [projects, query]);

  // 從 `/new` 進來的話,關掉燈箱要順手把網址收回 `/projects` —— 否則使用者按上一頁
  // 會回到「又自動打開燈箱」的同一個網址。
  const closeCreate = (): void => {
    setCreating(false);
    if (autoOpenCreate) navigate('/projects', { replace: true });
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.projectId);
    try {
      await services.projectsService.remove(confirmDelete.projectId);
      setConfirmDelete(null);
      await refresh();
    } catch {
      setError(t('list.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AppShell mainPadding={0}>
      <main style={{ display: 'block' }}>
        {/* R292.11 — 頁首改共用 `PageTopBar`(D3 `.topbar`)。 */}
        <PageTopBar
          title={t('list.title')}
          actions={
            <>
          {/* 排序(T12 / docs/50)— 接 BE sort_by/order;改選即重新 fetch。
              R393(CIS「下拉式選單」禁原生 `<select>`)→ HaiSelect 工具列版
              (D3 `.pj-sortbtn`:sortDown 圖示 +「排序：<b>最近活躍</b>」+ cv)。
              R279 那顆自畫 chevron 隨原生 select 一起退場。 */}
          <HaiSelect<SortKey>
            variant="toolbar"
            icon="sortDown"
            prefix={t('list.sortPrefix')}
            value={sortKey}
            onChange={(v) => setSortKey(v)}
            options={[
              { value: 'recent', label: t('list.sortRecent') },
              { value: 'created', label: t('list.sortCreated') },
              { value: 'name', label: t('list.sortName') },
            ]}
            style={sortSelectStyle}
            data-testid="project-sort-select"
          />

          {/* 網格 / 列表視圖切換(對齊 M-12 mockup line 198-205)*/}
          <div style={viewToggleStyle} role="group" aria-label={t('list.viewToggleLabel')}>
            <button
              type="button"
              onClick={() => setView('grid')}
              aria-pressed={view === 'grid'}
              title={t('list.viewGrid')}
              aria-label={t('list.viewGrid')}
              data-testid="project-view-grid"
              style={viewToggleBtnStyle(view === 'grid')}
            >
              <HaiIcon name="vGrid" size={13} />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              title={t('list.viewList')}
              aria-label={t('list.viewList')}
              data-testid="project-view-list"
              style={viewToggleBtnStyle(view === 'list')}
            >
              <HaiIcon name="vList" size={13} />
            </button>
          </div>
              {/* CIS 頁面主行動:實心 teal `.btn-solid`(高 34 / 12.5px 700 / 左 13px ＋)。
                  走 class(hover / 停用態在 global.css;inline 會蓋掉 :hover)。 */}
              <button
                type="button"
                className="hai-btn-solid"
                onClick={() => setCreating(true)}
                data-testid="project-create-cta"
              >
                <HaiIcon name="plus" size={13} />
                {t('list.createCta')}
              </button>
            </>
          }
        >
          {/* R397 — 容器用 `<label>`(點 icon / padding 也聚焦 input);聚焦變色走全站
              `.hai-searchbox:focus-within`(框線歸 class,inline 寫了就蓋掉它)。 */}
          <label
            className="hai-searchbox"
            style={searchBoxStyle}
            data-testid="project-search-box"
          >
            <span aria-hidden style={searchIconStyle}>
              <HaiIcon name="search" size={14} />
            </span>
            <input
              type="text"
              placeholder={t('list.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={searchInputStyle}
              aria-label={t('list.searchPlaceholder')}
            />
          </label>
        </PageTopBar>
        <div style={pageBodyStyle}>
        {/* R292.16(owner:「首頁 › 專案列表 / 管理所有正在進行…這一段都刪除」)—
            麵包屑與說明句退場:頁首那條已經有標題,側欄也標著現在在哪。 */}


        {/* Result count */}
        {filtered && (
          <div style={countLineStyle}>
            {t('list.countLabel', { count: filtered.length })}
          </div>
        )}

        {error && (
          <div role="alert" style={errorBannerStyle}>
            {error === t('list.deleteError') ? error : t('list.loadError')}
          </div>
        )}

        {projects === null ? (
          <div role="status" style={loadingStyle}>
            {t('list.loading')}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => setCreating(true)} />
        ) : view === 'grid' ? (
          <div style={gridStyle} data-testid="project-list">
            {filtered?.map((project) => (
              <ProjectCard
                key={project.projectId}
                project={project}
                brandKit={
                  project.brandKitId
                    ? brandKitById.get(project.brandKitId) ?? null
                    : null
                }
                onOpen={() => navigate(`/canvas/${project.projectId}`)}
                onEdit={() => setEditing(project)}
                onDelete={() => setConfirmDelete(project)}
                deleting={deletingId === project.projectId}
              />
            ))}
            {/* + 建立新專案 dashed tile(R293:改開燈箱,不再導頁)*/}
            <button
              type="button"
              onClick={() => setCreating(true)}
              style={dashedTileStyle}
              data-testid="project-create-dashed-tile"
              aria-label={t('list.dashedTileLabel')}
            >
              <span aria-hidden style={dashedTilePlusBoxStyle}>
                <HaiIcon name="plus" size={20} />
              </span>
              <span style={dashedTileLabelStyle}>{t('list.dashedTileLabel')}</span>
            </button>
          </div>
        ) : (
          <div style={listStyle} data-testid="project-list">
            {filtered?.map((project) => (
              <ProjectRow
                key={project.projectId}
                project={project}
                brandKit={
                  project.brandKitId
                    ? brandKitById.get(project.brandKitId) ?? null
                    : null
                }
                onOpen={() => navigate(`/canvas/${project.projectId}`)}
                onEdit={() => setEditing(project)}
                onDelete={() => setConfirmDelete(project)}
                deleting={deletingId === project.projectId}
              />
            ))}
            {/* + 建立新專案 dashed row(list view 等價 dashed tile)*/}
            <button
              type="button"
              onClick={() => setCreating(true)}
              style={dashedRowStyle}
              data-testid="project-create-dashed-tile"
              aria-label={t('list.dashedTileLabel')}
            >
              <span aria-hidden style={dashedRowPlusBoxStyle}>
                <HaiIcon name="plus" size={14} />
              </span>
              <span style={dashedTileLabelStyle}>{t('list.dashedTileLabel')}</span>
            </button>
          </div>
        )}

        {creating && <ProjectCreateDialog onClose={closeCreate} />}
        {confirmDelete && (
          <ProjectDeleteDialog
            project={confirmDelete}
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() => void handleDeleteConfirm()}
          />
        )}
        {editing && (
          <ProjectEditDialog
            project={editing}
            onClose={() => setEditing(null)}
            onSaved={(updated) =>
              setProjects((prev) =>
                prev ? prev.map((p) => (p.projectId === updated.projectId ? updated : p)) : prev,
              )
            }
          />
        )}
        </div>
      </main>
    </AppShell>
  );
}

/**
 * R397(owner 2026-10-07:「第一次使用都是空的,原本是請他回首頁建立 —— 改成直接建立」)
 * —— 空態 CTA 直接開建立燈箱(與右上角「建立專案」同一個動作),不再把人導回首頁。
 */
function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation('projects');
  return (
    <div
      role="status"
      data-testid="project-empty"
      style={{
        padding: '64px 32px',
        textAlign: 'center',
        background: 'var(--ink-900)',
        border: '1px dashed var(--hairline-strong)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div
        style={{
          color: 'var(--ink-100)',
          fontSize: 'var(--text-xl)',
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        {t('list.empty.title')}
      </div>
      <p
        style={{
          color: 'var(--ink-400)',
          fontSize: 'var(--text-md)',
          margin: '0 0 24px',
        }}
      >
        {t('list.empty.hint')}
      </p>
      <button type="button" onClick={onCreate} style={primaryBtnStyle} data-testid="project-empty-create">
        {t('list.empty.cta')}
      </button>
    </div>
  );
}

/** R292.11 — 頁首滿版之後,內容區自己補回原本的留白。 */
const pageBodyStyle: CSSProperties = { padding: '24px 40px 80px' };
